import { AddAPhotoRounded, CampaignRounded, FavoriteBorderRounded } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import axios, { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { StatusCodes } from 'http-status-codes'
import { omit } from 'lodash'
import { nanoid } from 'nanoid'
import { ChangeEvent, FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import {
  animalAdoptionOfferTypeOrder,
  getReadableAnimalAdoptionOfferType,
  getColorForAnimalAdoptionStatus,
  getColorForAnimalVaccineStatus,
  getReadableAnimalAdoptionStatus,
  getReadableAnimalBreedTitle,
  getReadableAnimalHealthTestStatus,
  getReadableAnimalVaccineStatus,
  getReadableSpecies,
  getReadableTattooLocation,
} from '../../domain/Animal/AnimalDisplay'
import { catBreeds, dogBreeds } from '../../domain/Animal/Breeds'
import { AnimalsClient } from '../../interactors/clients/AnimalsClient'
import {
  AnimalAdoptionDtoOfferType,
  AnimalBreedDtoBreedTitle,
  AnimalDtoSpecies,
  AnimalHealthDtoFIV,
  AnimalHealthDtoFeLV,
  AnimalHealthOperationDto,
  AnimalHealthOperationDtoType,
  AnimalHealthTestResultDtoType,
  AnimalHealthDtoSterilizedOrNeutered,
  CreateAnimalBodyDto,
  CreateAnimalBodyDtoAdoptionStatus,
  CreateAnimalBodyDtoSpecies,
  CreateAnimalBodyDtoVaccinationStatus,
  TattooDtoLocation,
} from '../../interactors/gen/backendClient'
import { useAccountsStore } from '../../store/AccountsStore'
import { useDashboardLoaderStore } from '../../store/DashboardLoaderStore'
import { useGlobalSnackbarStore } from '../../store/GlobalSnackBarStore'
import { readableAxiosError } from '../../utils/axios'
import { isValidPastDateCoherence } from '../../utils/date/isValidPastDateCoherence'
import { omitEmptyString } from '../../utils/omitEmptyString'
import { ControlledAutoCompleteWithCustomValue } from '../common/ControlledAutocompleteWithCustomValue'
import { ControlledDateField } from '../common/ControlledDateField'
import { ColoredChipSelectField } from '../common/ColoredChipSelectField'
import { ControlledMultipleFreeSoloAutoCompleteWithCategories } from '../common/ControlledMultipleFreeSoloAutocompleteWithCategories'
import { ControlledSelectField } from '../common/ControlledSelectField'
import { PATHS } from '../PATHS'
import { parseMixedBreed } from '../../utils/parseMixedBreed'
import { colors, theme } from '../theme'
import { surfaceCardSx } from '../common/surfaceCardSx'
import { AnimalSheetPopUp } from './AnimalSheetPopUp'

// We need to omit, if not it does not work
type CreateAnimalFormParams = Omit<
  CreateAnimalBodyDto,
  | 'identificationNumber'
  | 'birthday'
  | 'status'
  | 'species'
  | 'vaccinationStatus'
  | 'adoptionStatus'
  | 'sterilizedOrNeutered'
  | 'breedData'
> & {
  identificationNumber: number | ''
  tattooLocation?: string
  tattooNumber?: string
  adoptionStatus: CreateAnimalBodyDtoAdoptionStatus | ''
  species: CreateAnimalBodyDtoSpecies | ''
  vaccinationStatus: CreateAnimalBodyDtoVaccinationStatus | ''
  sterilizedOrNeutered: AnimalHealthDtoSterilizedOrNeutered | ''
  birthdayDate: string
  // 1 = true, 2 = false --- Needed because Select component does not support boolean
  isApproximateBirthdayDate?: number
  breedTitle?: AnimalBreedDtoBreedTitle
  breed?: string
  fatherBreed?: string
  motherBreed?: string
  characteristics?: Array<string>
  litterNumber?: string
  numberOfAnimalsInLitter?: number | ''
  isPedigreeRegistered?: boolean
  offerType?: AnimalAdoptionDtoOfferType | ''
  priceInEuros?: number | ''
  FIV?: AnimalHealthDtoFIV | ''
  FeLV?: AnimalHealthDtoFeLV | ''
  shareOnPetso?: boolean
  shareOnWamiz?: boolean
}

type AddAnimalMode = 'intake' | 'announce'

export const AddAnimalScreen: FC = () => {
  const accountsStore = useAccountsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const { setIsLoading } = useDashboardLoaderStore()
  const navigate = useNavigate()
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateAnimalFormParams>({
    defaultValues: {
      name: '',
      takingChargeByTheCustomerAt: '',
      lifeBeforeAssociation: '',
      identificationNumber: '',
      tattooLocation: '',
      tattooNumber: '',
      adoptionStatus: '',
      description: '',
      species: '',
      vaccinationStatus: 'unknown',
      sterilizedOrNeutered: '',
      birthdayDate: '',
      isApproximateBirthdayDate: undefined,
      breedTitle: undefined,
      breed: '',
      fatherBreed: '',
      motherBreed: '',
      characteristics: [],
      litterNumber: '',
      numberOfAnimalsInLitter: '',
      isPedigreeRegistered: false,
      offerType: AnimalAdoptionDtoOfferType.HealthCostCompensation,
      priceInEuros: '',
      FIV: '',
      FeLV: '',
      shareOnPetso: true,
      shareOnWamiz: true,
    },
    mode: 'onBlur',
  })

  const [serverError, setServerError] = useState('')
  const [mode, setMode] = useState<AddAnimalMode>('intake')
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('')
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  useEffect(() => {
    if (!selectedImage) {
      setImagePreviewUrl('')
      return
    }

    const objectUrl = URL.createObjectURL(selectedImage)
    setImagePreviewUrl(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [selectedImage])

  const handleImageSelection = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (!file) {
      return
    }

    setSelectedImage(file)
    event.target.value = ''
  }

  const onSubmit = async (data: CreateAnimalFormParams, stay?: boolean) => {
    setServerError('')

    const requiresIdentificationForPublication =
      mode === 'announce' && ['cat', 'dog', 'ferret'].includes(data.species || '')

    if (mode === 'announce' && !selectedImage) {
      setServerError('Une photo de profil est requise pour préparer une annonce.')
      return
    }

    if (
      requiresIdentificationForPublication &&
      !data.identificationNumber &&
      !(data.tattooNumber && `${data.tattooNumber}`.trim() !== '')
    ) {
      setServerError('Pour publier une annonce, renseignez une identification ou un tatouage.')
      return
    }

    setIsLoading(true)

    const formattedData = omitEmptyString({
      ...omit(data, 'birthdayDate', 'isApproximateBirthdayDate', 'breedTitle', 'breed', 'offerType', 'priceInEuros'),
      adoptionStatus:
        mode === 'announce' ? CreateAnimalBodyDtoAdoptionStatus.Adoptable : data.adoptionStatus || undefined,
      birthday: {
        date: data.birthdayDate,
        isApproximate: data.isApproximateBirthdayDate
          ? data.isApproximateBirthdayDate === 1
            ? true
            : false
          : undefined,
      },
      breed:
        data.breedTitle === AnimalBreedDtoBreedTitle.MixedBreed
          ? parseMixedBreed(data.fatherBreed, data.motherBreed)
          : data.breed,
      breedData: {
        breedTitle: data.breedTitle as AnimalBreedDtoBreedTitle,
        breed:
          data.breedTitle === AnimalBreedDtoBreedTitle.MixedBreed
            ? parseMixedBreed(data.fatherBreed, data.motherBreed)
            : data.breed,
        litterNumber: data.litterNumber,
        numberOfAnimalsInLitter:
          data.numberOfAnimalsInLitter === '' || data.numberOfAnimalsInLitter == null
            ? undefined
            : Number(data.numberOfAnimalsInLitter),
        isPedigreeRegistered: data.isPedigreeRegistered,
      },
    }) as CreateAnimalBodyDto

    await AnimalsClient.createAnimal(formattedData)
      .then(async (animal) => {
        let uploadedProfileImageKey: string | undefined

        if (selectedImage) {
          const uploadedImageUrl = await AnimalsClient.uploadProfileImage(animal.id, selectedImage)
          uploadedProfileImageKey = uploadedImageUrl.split('/').pop()
        }

        if (mode === 'announce') {
          let refreshedAnimal = await AnimalsClient.getAnimal(animal.id)

          if (uploadedProfileImageKey) {
            let retryCount = 0

            while (
              (!refreshedAnimal.images?.profileImageKey || refreshedAnimal.images.profileImageKey === '') &&
              retryCount < 4
            ) {
              await new Promise((resolve) => setTimeout(resolve, 350))
              refreshedAnimal = await AnimalsClient.getAnimal(animal.id)
              retryCount += 1
            }

            refreshedAnimal = {
              ...refreshedAnimal,
              images: {
                ...refreshedAnimal.images,
                profileImageKey: refreshedAnimal.images?.profileImageKey || uploadedProfileImageKey,
              },
            }
          }

          const healthOperations: AnimalHealthOperationDto[] = [...(refreshedAnimal.health?.operations || [])]

          if (data.FeLV) {
            healthOperations.push({
              id: nanoid(),
              date: dayjs().format('DD/MM/YYYY'),
              type: [AnimalHealthOperationDtoType.FeLv],
              testResults: [
                {
                  type: AnimalHealthTestResultDtoType.FeLv,
                  result: data.FeLV,
                },
              ],
            })
          }

          if (data.FIV) {
            healthOperations.push({
              id: nanoid(),
              date: dayjs().format('DD/MM/YYYY'),
              type: [AnimalHealthOperationDtoType.Fiv],
              testResults: [
                {
                  type: AnimalHealthTestResultDtoType.Fiv,
                  result: data.FIV,
                },
              ],
            })
          }

          if (data.sterilizedOrNeutered === 'true') {
            const hasSterilizationOperation = healthOperations.some((operation) =>
              operation.type.includes(AnimalHealthOperationDtoType.SterilizedOrNeutered)
            )

            if (!hasSterilizationOperation) {
              healthOperations.push({
                id: nanoid(),
                type: [AnimalHealthOperationDtoType.SterilizedOrNeutered],
                date: '',
              })
            }
          }

          await AnimalsClient.editAnimal({
            ...refreshedAnimal,
            adoption: {
              ...refreshedAnimal.adoption,
              offerType: (data.offerType || undefined) as AnimalAdoptionDtoOfferType | undefined,
              priceInEuros:
                data.offerType === AnimalAdoptionDtoOfferType.Donation
                  ? 0
                  : data.priceInEuros === '' || data.priceInEuros == null
                  ? undefined
                  : Number(data.priceInEuros),
            },
            health: {
              ...refreshedAnimal.health,
              FIV: (data.FIV || undefined) as AnimalHealthDtoFIV | undefined,
              FeLV: (data.FeLV || undefined) as AnimalHealthDtoFeLV | undefined,
              operations: healthOperations,
            },
            shareAnnounce: {
              petso: data.shareOnPetso ?? true,
              wamiz: data.shareOnWamiz ?? true,
            },
          })
        }

        globalSnackbarStore.triggerSuccessMessage(`Le profil de ${animal.name} a été créé.`)

        if (!stay) {
          navigate(`${PATHS.animals.absolute}/${animal.id}`)
        } else {
          setValue('name', '')
          setValue('identificationNumber', '')
          setValue('tattooNumber', '')
          setValue('adoptionStatus', CreateAnimalBodyDtoAdoptionStatus.Adoptable)
          setValue('offerType', AnimalAdoptionDtoOfferType.HealthCostCompensation)
          setValue('priceInEuros', '')
          setValue('shareOnPetso', true)
          setValue('shareOnWamiz', true)
          setSelectedImage(null)
        }
      })
      .catch((error: Error | AxiosError) => {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === StatusCodes.CONFLICT) {
            setServerError('Cet animal existe déjà.')
            return
          }
        }

        setServerError(readableAxiosError(error).join(' '))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const species = accountsStore.connectedCustomer?.preferences.species

  useEffect(() => {
    if (species?.length === 1) {
      setValue('species', species[0])
    }
  }, [species])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  useEffect(() => {
    if (mode === 'announce') {
      setValue('adoptionStatus', watch('adoptionStatus') || CreateAnimalBodyDtoAdoptionStatus.Adoptable)
    }
  }, [mode, setValue, watch])

  const birthdayDate = watch('birthdayDate')
  const watchBreedTitle = watch('breedTitle')
  const watchSpecies = watch('species')
  const watchName = watch('name')
  const watchApproximateBirthday = watch('isApproximateBirthdayDate')
  const watchIdentificationNumber = watch('identificationNumber')
  const watchTattooNumber = watch('tattooNumber')
  const watchOfferType = watch('offerType')
  const watchPriceInEuros = watch('priceInEuros')

  useEffect(() => {
    if (watchOfferType === AnimalAdoptionDtoOfferType.Donation) {
      setValue('priceInEuros', 0, { shouldValidate: true })
    }
  }, [setValue, watchOfferType])
  useEffect(() => {
    if (
      watchOfferType === AnimalAdoptionDtoOfferType.FreeAmount &&
      (watchPriceInEuros === '' || watchPriceInEuros == null || Number(watchPriceInEuros) < 1)
    ) {
      setValue('priceInEuros', 1, { shouldValidate: true })
    }
  }, [setValue, watchOfferType, watchPriceInEuros])
  const isAnnounceMode = mode === 'announce'
  const requiresIdentificationForPublication = isAnnounceMode && ['cat', 'dog', 'ferret'].includes(watchSpecies || '')
  const hasPublicationIdentification =
    Boolean(watchIdentificationNumber) || Boolean(watchTattooNumber && `${watchTattooNumber}`.trim() !== '')
  const isDonationOffer = watchOfferType === AnimalAdoptionDtoOfferType.Donation
  const isFreeAmountOffer = watchOfferType === AnimalAdoptionDtoOfferType.FreeAmount
  const priceFieldLabel = isFreeAmountOffer ? 'Montant minimum (min. 1€)' : 'Montant *'
  const isPublishAnnounceDisabled =
    isAnnounceMode &&
    (!selectedImage ||
      !watchName?.trim() ||
      !watchSpecies ||
      !birthdayDate ||
      watchApproximateBirthday == null ||
      !watchOfferType ||
      (!isDonationOffer && !isFreeAmountOffer && (watchPriceInEuros === '' || watchPriceInEuros == null)) ||
      (requiresIdentificationForPublication && !hasPublicationIdentification))

  const breedOptions =
    watchSpecies === 'cat'
      ? catBreeds.map((breed) => ({ label: breed, value: breed }))
      : dogBreeds.map((breed) => ({ label: breed, value: breed }))

  const spacingItem = { marginTop: 2 }

  const handleDisabledPublishClick = () => {
    if (isPublishAnnounceDisabled) {
      globalSnackbarStore.triggerErrorMessage('Des informations essentielles sont manquantes')
    }
  }

  return (
    <Box sx={{ width: '900px', maxWidth: '90%', marginX: 'auto', marginY: '5rem' }}>
      <Typography variant="h4">🐱 Ajouter un Animal</Typography>
      <Typography sx={{ mt: 1, color: '#7A8197', fontWeight: 600 }}>
        Choisissez si vous souhaitez créer une prise en charge complète ou aller directement vers un profil prêt à
        diffuser.
      </Typography>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setMode('intake')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                setMode('intake')
              }
            }}
            sx={{
              p: 2.25,
              cursor: 'pointer',
              outline: 'none',
              borderRadius: 4,
              transition: 'all 0.18s ease',
              border: mode === 'intake' ? `1px solid ${colors.cello}` : '1px solid #E4E8F2',
              backgroundColor: mode === 'intake' ? 'rgba(243, 247, 252, 0.96)' : 'rgba(255,255,255,0.78)',
              boxShadow:
                mode === 'intake' ? '0 12px 30px rgba(32, 56, 100, 0.08)' : '0 1px 2px rgba(32, 56, 100, 0.04)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(58, 86, 119, 0.1)',
                  color: colors.cello,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FavoriteBorderRounded />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, color: colors.cello }}>Ajouter une prise en charge</Typography>
                <Typography sx={{ mt: 0.5, color: '#7A8197', fontSize: 14.5 }}>
                  Crée la fiche d&apos;un animal avec son contexte de prise en charge, ou{' '}
                  <AnimalSheetPopUp
                    buttonLabel="multi-import depuis un fichier"
                    buttonSx={{
                      p: 0,
                      minWidth: 0,
                      fontSize: 13,
                      fontWeight: 700,
                      color: colors.cello,
                      textTransform: 'none',
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecorationThickness: '2px',
                      },
                    }}
                  />
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setMode('announce')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                setMode('announce')
              }
            }}
            sx={{
              p: 2.25,
              cursor: 'pointer',
              outline: 'none',
              borderRadius: 4,
              transition: 'all 0.18s ease',
              border: mode === 'announce' ? `1px solid ${colors.cello}` : '1px solid #E4E8F2',
              backgroundColor: mode === 'announce' ? 'rgba(243, 247, 252, 0.96)' : 'rgba(255,255,255,0.78)',
              boxShadow:
                mode === 'announce' ? '0 12px 30px rgba(32, 56, 100, 0.08)' : '0 1px 2px rgba(32, 56, 100, 0.04)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(58, 86, 119, 0.1)',
                  color: colors.cello,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CampaignRounded />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, color: colors.cello }}>Publier une annonce</Typography>
                <Typography sx={{ mt: 0.5, color: '#7A8197', fontSize: 14.5 }}>
                  Version resserrée sur les champs clés pour créer un profil publiable rapidement.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <form onSubmit={handleSubmit((data) => onSubmit(data))}>
        {isAnnounceMode && (
          <Grid container spacing={2.25} sx={{ mt: 0.5, mb: 0.5 }}>
            <Grid item xs={12} md={3.25}>
              <Box
                sx={{
                  ...surfaceCardSx,
                  height: '100%',
                  minHeight: { xs: 180, md: 248 },
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  textAlign: 'center',
                }}
              >
                <Box
                  component="label"
                  sx={{ position: 'relative', cursor: 'pointer', borderRadius: '50%', lineHeight: 0 }}
                >
                  <Avatar
                    src={imagePreviewUrl}
                    sx={{
                      width: { xs: 92, md: 122 },
                      height: { xs: 92, md: 122 },
                      border: '1px solid #E4E8F2',
                      backgroundColor: '#F6F8FC',
                      color: '#7A8197',
                    }}
                  >
                    <AddAPhotoRounded sx={{ fontSize: { xs: 28, md: 34 } }} />
                  </Avatar>
                  <Button
                    component="label"
                    variant="contained"
                    size="small"
                    sx={{
                      position: 'absolute',
                      right: -10,
                      bottom: -10,
                      minWidth: 0,
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      p: 0,
                      boxShadow: 'none',
                    }}
                  >
                    <AddAPhotoRounded sx={{ fontSize: 18 }} />
                    <input hidden accept="image/*" type="file" onChange={handleImageSelection} />
                  </Button>
                  <input hidden accept="image/*" type="file" onChange={handleImageSelection} />
                </Box>

                <Box>
                  <Typography sx={{ fontWeight: 800, color: colors.cello }}>
                    Photo de l&apos;annonce{' '}
                    <Box component="span" sx={{ color: theme.palette.error.main }}>
                      *
                    </Box>
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: '#7A8197', fontSize: 13.5 }}>
                    Ajoutez la photo principale affichée sur la fiche publique.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={8.75}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      id="first-name-input-announce"
                      label="Nom"
                      type="text"
                      required
                      fullWidth
                      {...register('name', { required: 'Le nom est requis' })}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ControlledSelectField
                      control={control}
                      error={errors.species}
                      fieldName="species"
                      label="Espèce"
                      options={
                        species
                          ? Object.values(species).map((status: AnimalDtoSpecies) => ({
                              label: getReadableSpecies(status, { withEmoji: true }),
                              value: status,
                            }))
                          : []
                      }
                      requiredRule="L'espèce est requise."
                      disabled={species?.length === 1}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ControlledSelectField
                      control={control}
                      error={errors.sex}
                      fieldName="sex"
                      label="Sexe"
                      options={[
                        { label: 'Mâle', value: 'male' },
                        { label: 'Femelle', value: 'female' },
                      ]}
                      requiredRule={undefined}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ControlledDateField
                      control={control}
                      validate={(value) => isValidPastDateCoherence(value) || !value}
                      error={errors.sex}
                      fieldName={'birthdayDate'}
                      label="Date de Naissance *"
                      requiredRule="La date de naissance est requise pour publier une annonce."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ControlledSelectField
                      control={control}
                      error={errors.isApproximateBirthdayDate}
                      fieldName="isApproximateBirthdayDate"
                      label="Est-elle précise ?"
                      options={[
                        { label: 'Non', value: 1 },
                        { label: 'Oui', value: 2 },
                      ]}
                      requiredRule={
                        birthdayDate ? 'Vous devez préciser si la date de naissance est précise ou non.' : ''
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <ControlledSelectField
                      control={control}
                      error={errors.breedTitle}
                      fieldName="breedTitle"
                      label="Type"
                      options={Object.values(AnimalBreedDtoBreedTitle).map((value: AnimalBreedDtoBreedTitle) => ({
                        label: getReadableAnimalBreedTitle(value),
                        value: value,
                      }))}
                      requiredRule=""
                    />
                  </Grid>
                  {watchBreedTitle !== AnimalBreedDtoBreedTitle.MixedBreed && (
                    <Grid item xs={12} sm={8}>
                      {watchSpecies === 'cat' || watchSpecies === 'dog' ? (
                        <ControlledAutoCompleteWithCustomValue
                          control={control}
                          fieldName="breed"
                          error={errors.breed}
                          aria-label="Race"
                          label="Race"
                          requiredRule={undefined}
                          options={breedOptions}
                          defaultValue=""
                          freeSolo={watchSpecies === 'dog'}
                          helperText={
                            watchSpecies === 'dog'
                              ? `Si la race n'est pas dans les propositions, écrivez là et appuyez sur la touche entrée`
                              : ''
                          }
                        />
                      ) : (
                        <TextField
                          id="breed-input-announce"
                          label={getReadableAnimalBreedTitle(watchBreedTitle as AnimalBreedDtoBreedTitle) || 'Race'}
                          type="text"
                          fullWidth
                          {...register('breed')}
                          error={!!errors.breed}
                        />
                      )}
                    </Grid>
                  )}
                  {watchBreedTitle === AnimalBreedDtoBreedTitle.MixedBreed && (
                    <>
                      <Grid item xs={12} sm={6}>
                        {watchSpecies === 'cat' || watchSpecies === 'dog' ? (
                          <ControlledAutoCompleteWithCustomValue
                            control={control}
                            fieldName="fatherBreed"
                            error={errors.fatherBreed}
                            aria-label="Race du père"
                            label="Race du père"
                            requiredRule={undefined}
                            options={breedOptions}
                            defaultValue=""
                          />
                        ) : (
                          <TextField
                            id="father-breed-input-announce"
                            label="Race du père"
                            type="text"
                            fullWidth
                            {...register('fatherBreed')}
                            error={!!errors.fatherBreed}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {watchSpecies === 'cat' || watchSpecies === 'dog' ? (
                          <ControlledAutoCompleteWithCustomValue
                            control={control}
                            fieldName="motherBreed"
                            error={errors.motherBreed}
                            aria-label="Race de la mère"
                            label="Race de la mère"
                            requiredRule={undefined}
                            options={breedOptions}
                            defaultValue=""
                          />
                        ) : (
                          <TextField
                            id="mother-breed-input-announce"
                            label="Race de la mère"
                            type="text"
                            fullWidth
                            {...register('motherBreed')}
                            error={!!errors.motherBreed}
                          />
                        )}
                      </Grid>
                    </>
                  )}
                  {!isDesktop && (
                    <>
                      <Grid item xs={12}>
                        <ColoredChipSelectField
                          control={control}
                          error={errors.adoptionStatus}
                          fieldName="adoptionStatus"
                          label="Statut d'adoption"
                          chipTone="important"
                          options={Object.values(CreateAnimalBodyDtoAdoptionStatus)
                            .filter((status) => status !== CreateAnimalBodyDtoAdoptionStatus['ToBeFilled'])
                            .map((status: CreateAnimalBodyDtoAdoptionStatus) => ({
                              label: getReadableAnimalAdoptionStatus(status),
                              value: status,
                              color: getColorForAnimalAdoptionStatus(status),
                            }))}
                          requiredRule="Le statut est requis"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledSelectField
                          control={control}
                          error={errors.offerType}
                          fieldName="offerType"
                          label="Type d'offre"
                          options={animalAdoptionOfferTypeOrder.map((field: AnimalAdoptionDtoOfferType) => ({
                            label: getReadableAnimalAdoptionOfferType(field),
                            value: field,
                          }))}
                          requiredRule="Le type d'offre est requis"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          id="price-input-announce-mobile"
                          label={priceFieldLabel}
                          type="number"
                          inputProps={{
                            step: '0.01',
                            inputMode: 'decimal',
                            pattern: '[0-9]*',
                            min: isFreeAmountOffer ? 1 : 0,
                          }}
                          disabled={isDonationOffer}
                          fullWidth
                          {...register('priceInEuros', {
                            validate: (value) => {
                              if (isDonationOffer) return true
                              if (isFreeAmountOffer && (value === '' || value == null))
                                return 'Le montant minimum est requis'
                              if (isFreeAmountOffer && Number(value) < 1) {
                                return 'Le montant minimum doit être au moins de 1€'
                              }
                              if (value === '' || value == null) return 'Le montant est requis'
                              return Number(value) >= 0 || 'Le montant doit être positif'
                            },
                          })}
                          error={!!errors.priceInEuros}
                          helperText={errors.priceInEuros?.message}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">€</InputAdornment>,
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        )}

        {isAnnounceMode && isDesktop && (
          <Grid container spacing={1.5} sx={spacingItem}>
            <Grid item xs={12}>
              <ColoredChipSelectField
                control={control}
                error={errors.adoptionStatus}
                fieldName="adoptionStatus"
                label="Statut d'adoption"
                chipTone="important"
                options={Object.values(CreateAnimalBodyDtoAdoptionStatus)
                  .filter((status) => status !== CreateAnimalBodyDtoAdoptionStatus['ToBeFilled'])
                  .map((status: CreateAnimalBodyDtoAdoptionStatus) => ({
                    label: getReadableAnimalAdoptionStatus(status),
                    value: status,
                    color: getColorForAnimalAdoptionStatus(status),
                  }))}
                requiredRule="Le statut est requis"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ControlledSelectField
                control={control}
                error={errors.offerType}
                fieldName="offerType"
                label="Type d'offre"
                options={animalAdoptionOfferTypeOrder.map((field: AnimalAdoptionDtoOfferType) => ({
                  label: getReadableAnimalAdoptionOfferType(field),
                  value: field,
                }))}
                requiredRule="Le type d'offre est requis"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="price-input-announce-desktop"
                label={priceFieldLabel}
                type="number"
                inputProps={{
                  step: '0.01',
                  inputMode: 'decimal',
                  pattern: '[0-9]*',
                  min: isFreeAmountOffer ? 1 : 0,
                }}
                disabled={isDonationOffer}
                fullWidth
                {...register('priceInEuros', {
                  validate: (value) => {
                    if (isDonationOffer) return true
                    if (isFreeAmountOffer && (value === '' || value == null)) return 'Le montant minimum est requis'
                    if (isFreeAmountOffer && Number(value) < 1) {
                      return 'Le montant minimum doit être au moins de 1€'
                    }
                    if (value === '' || value == null) return 'Le montant est requis'
                    return Number(value) >= 0 || 'Le montant doit être positif'
                  },
                })}
                error={!!errors.priceInEuros}
                helperText={errors.priceInEuros?.message}
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        )}

        {!isAnnounceMode && (
          <>
            <TextField
              id="first-name-input"
              label="Nom"
              type="text"
              required
              fullWidth
              {...register('name', { required: 'Le nom est requis' })}
              sx={spacingItem}
              error={!!errors.name}
              helperText={errors.name?.message}
            />

            <ControlledSelectField
              sx={spacingItem}
              control={control}
              error={errors.species}
              fieldName="species"
              label="Espèce"
              options={
                species
                  ? Object.values(species).map((status: AnimalDtoSpecies) => ({
                      label: getReadableSpecies(status, { withEmoji: true }),
                      value: status,
                    }))
                  : []
              }
              requiredRule="L'espèce est requise."
              disabled={species?.length === 1}
            />

            <ControlledSelectField
              sx={spacingItem}
              control={control}
              error={errors.sex}
              fieldName="sex"
              label="Sexe"
              options={[
                { label: 'Mâle', value: 'male' },
                { label: 'Femelle', value: 'female' },
              ]}
              requiredRule=""
            />
          </>
        )}

        {!isAnnounceMode && (
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <ControlledDateField
                sx={spacingItem}
                control={control}
                validate={(value) => isValidPastDateCoherence(value) || !value}
                error={errors.sex}
                fieldName={'birthdayDate'}
                label="Date de Naissance"
                requiredRule={undefined}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledSelectField
                sx={spacingItem}
                control={control}
                error={errors.isApproximateBirthdayDate}
                fieldName="isApproximateBirthdayDate"
                label="Est-elle précise ?"
                options={[
                  { label: 'Non', value: 1 },
                  { label: 'Oui', value: 2 },
                ]}
                requiredRule={birthdayDate ? 'Vous devez préciser si la date de naissance est précise ou non.' : ''}
              />
            </Grid>
          </Grid>
        )}

        {!isAnnounceMode && (
          <ControlledSelectField
            sx={spacingItem}
            control={control}
            error={errors.adoptionStatus}
            fieldName="adoptionStatus"
            label="Statut"
            options={Object.values(CreateAnimalBodyDtoAdoptionStatus)
              .filter((status) => status !== CreateAnimalBodyDtoAdoptionStatus['ToBeFilled'])
              .map((status: CreateAnimalBodyDtoAdoptionStatus) => ({
                label: getReadableAnimalAdoptionStatus(status),
                value: status,
              }))}
            requiredRule="Le statut est requis"
          />
        )}

        {!isAnnounceMode && (
          <ControlledDateField
            sx={spacingItem}
            control={control}
            validate={(value) => isValidPastDateCoherence(value) || value === null}
            error={errors.takingChargeByTheCustomerAt}
            fieldName={'takingChargeByTheCustomerAt'}
            label="Date de Prise en Charge par l'association"
            requiredRule={undefined}
          />
        )}

        {!isAnnounceMode && (
          <TextField
            id="city-of-origin-input"
            label="Ville de prise en charge"
            type="text"
            fullWidth
            {...register('cityOfOrigin')}
            sx={spacingItem}
            error={!!errors.cityOfOrigin}
            helperText={errors.cityOfOrigin?.message}
          />
        )}

        {isAnnounceMode && (
          <Box
            sx={{
              ...surfaceCardSx,
              mt: 2,
              p: { xs: 1.75, md: 2.25 },
            }}
          >
            <Typography sx={{ fontWeight: 800, color: colors.cello, mb: 1.25 }}>Informations de santé</Typography>

            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  control={control}
                  error={errors.sterilizedOrNeutered}
                  fieldName="sterilizedOrNeutered"
                  label="Stérilisation / castration"
                  options={[
                    { label: 'Stérilisé ou castré', value: 'true' },
                    { label: 'Non stérilisé ou non castré', value: 'false' },
                    { label: 'Inconnu', value: 'unknown' },
                  ]}
                  requiredRule={undefined}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ColoredChipSelectField
                  control={control}
                  error={errors.vaccinationStatus}
                  fieldName="vaccinationStatus"
                  label="💉 Statut vaccinal"
                  options={Object.values(CreateAnimalBodyDtoVaccinationStatus).map(
                    (status: CreateAnimalBodyDtoVaccinationStatus) => ({
                      label: getReadableAnimalVaccineStatus(status),
                      value: status,
                      color: getColorForAnimalVaccineStatus(status),
                    })
                  )}
                  requiredRule={undefined}
                />
              </Grid>
              {watchSpecies === 'cat' && (
                <>
                  <Grid item xs={12} md={6}>
                    <ControlledSelectField
                      control={control}
                      error={errors.FeLV}
                      fieldName="FeLV"
                      label="Test FeLV"
                      options={Object.values(AnimalHealthDtoFeLV).map((status: AnimalHealthDtoFeLV) => ({
                        label: getReadableAnimalHealthTestStatus(status),
                        value: status,
                      }))}
                      requiredRule={undefined}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ControlledSelectField
                      control={control}
                      error={errors.FIV}
                      fieldName="FIV"
                      label="Test FIV"
                      options={Object.values(AnimalHealthDtoFIV).map((status: AnimalHealthDtoFIV) => ({
                        label: getReadableAnimalHealthTestStatus(status),
                        value: status,
                      }))}
                      requiredRule={undefined}
                    />
                  </Grid>
                </>
              )}
              {requiresIdentificationForPublication && (
                <Grid item xs={12}>
                  <Typography sx={{ fontWeight: 700, color: colors.cello, fontSize: 14.5 }}>
                    Identification ou tatouage{' '}
                    <Box component="span" sx={{ color: theme.palette.error.main }}>
                      *
                    </Box>
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  id="identification-number-input-announce"
                  label="Identification"
                  type="text"
                  fullWidth
                  {...register('identificationNumber')}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }} />
              <Grid item xs={12} md={6}>
                <TextField
                  id="tattoo-number-input-announce"
                  label="Tatouage"
                  type="text"
                  fullWidth
                  {...register('tattooNumber')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  control={control}
                  error={errors.tattooLocation}
                  fieldName="tattooLocation"
                  label="Emplacement du tatouage"
                  options={Object.values(TattooDtoLocation).map((value: TattooDtoLocation) => ({
                    label: getReadableTattooLocation(value),
                    value: value,
                  }))}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2.25 }}>
              <Typography sx={{ fontWeight: 800, color: colors.cello }}>
                Portée & généalogie{' '}
                <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 500 }}>
                  (facultatif)
                </Box>
              </Typography>
            </Box>

            <Grid container spacing={1} sx={{ mt: 0.25 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  id="litter-number-input-announce"
                  label="N° de la portée"
                  type="text"
                  fullWidth
                  {...register('litterNumber')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  control={control}
                  error={errors.numberOfAnimalsInLitter}
                  fieldName="numberOfAnimalsInLitter"
                  label="Nombre d'animaux dans la portée"
                  options={[
                    { value: 1, label: '1' },
                    { value: 2, label: '2' },
                    { value: 3, label: '3' },
                    { value: 4, label: '4' },
                    { value: 5, label: '5' },
                    { value: 6, label: '6' },
                    { value: 7, label: '7' },
                    { value: 8, label: '8' },
                    { value: 9, label: '9' },
                    { value: 10, label: '10' },
                    { value: 11, label: '11' },
                    { value: 12, label: '12' },
                    { value: 13, label: '13' },
                    { value: 14, label: '14' },
                    { value: 15, label: '15' },
                  ]}
                  requiredRule={undefined}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox {...register('isPedigreeRegistered')} checked={watch('isPedigreeRegistered')} />}
                  label="Inscription à un livre généalogique"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {!isAnnounceMode && (
          <>
            <TextField
              id="identification-number-input"
              label="Identification"
              type="text"
              fullWidth
              {...register('identificationNumber')}
              sx={spacingItem}
            />

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  id="tattoo-number-input"
                  label="Tatouage"
                  type="text"
                  fullWidth
                  {...register('tattooNumber')}
                  sx={spacingItem}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledSelectField
                  sx={spacingItem}
                  control={control}
                  error={errors.tattooLocation}
                  fieldName="tattooLocation"
                  label="Emplacement du tatouage"
                  options={Object.values(TattooDtoLocation).map((value: TattooDtoLocation) => ({
                    label: getReadableTattooLocation(value),
                    value: value,
                  }))}
                />
              </Grid>
            </Grid>
          </>
        )}

        {!isAnnounceMode && (
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <ControlledSelectField
                sx={spacingItem}
                control={control}
                error={errors.breedTitle}
                fieldName="breedTitle"
                label="Titre"
                options={Object.values(AnimalBreedDtoBreedTitle).map((value: AnimalBreedDtoBreedTitle) => ({
                  label: getReadableAnimalBreedTitle(value),
                  value: value,
                }))}
                requiredRule={''}
              />
            </Grid>
            {watchBreedTitle !== AnimalBreedDtoBreedTitle.MixedBreed && (
              <Grid item sm={8} xs={12}>
                {watchSpecies === 'cat' || watchSpecies === 'dog' ? (
                  <ControlledAutoCompleteWithCustomValue
                    control={control}
                    fieldName="breed"
                    error={errors.breed}
                    aria-label="Race"
                    label="Race"
                    requiredRule="La race est requise"
                    options={breedOptions}
                    defaultValue=""
                    sx={spacingItem}
                    freeSolo={watchSpecies === 'dog'}
                    helperText={
                      watchSpecies === 'dog'
                        ? `Si la race n'est pas dans les propositions, écrivez là et appuyez sur la touche entrée`
                        : ''
                    }
                  />
                ) : (
                  <TextField
                    id="breed-input"
                    label={getReadableAnimalBreedTitle(watchBreedTitle as AnimalBreedDtoBreedTitle) || 'Race'}
                    type="text"
                    fullWidth
                    {...register('breed')}
                    sx={spacingItem}
                    error={!!errors.breed}
                    required
                  />
                )}
              </Grid>
            )}
            {watchBreedTitle === AnimalBreedDtoBreedTitle.MixedBreed && (
              <>
                <Grid item sm={4} xs={12}>
                  {watchSpecies === 'cat' || watchSpecies === 'dog' ? (
                    <ControlledAutoCompleteWithCustomValue
                      control={control}
                      fieldName="fatherBreed"
                      error={errors.fatherBreed}
                      aria-label="Race du père"
                      label="Race du père"
                      requiredRule="La race du père est requise"
                      options={breedOptions}
                      defaultValue=""
                      sx={spacingItem}
                    />
                  ) : (
                    <TextField
                      id="father-breed-input"
                      label="Race du père"
                      type="text"
                      fullWidth
                      {...register('fatherBreed')}
                      sx={spacingItem}
                      error={!!errors.fatherBreed}
                      required
                    />
                  )}
                </Grid>
                <Grid item sm={4} xs={12}>
                  {watchSpecies === 'cat' || watchSpecies === 'dog' ? (
                    <ControlledAutoCompleteWithCustomValue
                      control={control}
                      fieldName="motherBreed"
                      error={errors.motherBreed}
                      aria-label="Race de la mère"
                      label="Race de la mère"
                      requiredRule="La race de la mère est requise"
                      options={breedOptions}
                      defaultValue=""
                      sx={spacingItem}
                    />
                  ) : (
                    <TextField
                      id="mother-breed-input"
                      label="Race de la mère"
                      type="text"
                      fullWidth
                      {...register('motherBreed')}
                      sx={spacingItem}
                      error={!!errors.motherBreed}
                      required
                    />
                  )}
                </Grid>
              </>
            )}
          </Grid>
        )}

        {!isAnnounceMode && (
          <ControlledSelectField
            sx={spacingItem}
            control={control}
            error={errors.vaccinationStatus}
            fieldName="vaccinationStatus"
            label="💉 Primovaccination"
            options={Object.values(CreateAnimalBodyDtoVaccinationStatus).map(
              (status: CreateAnimalBodyDtoVaccinationStatus) => ({
                label: getReadableAnimalVaccineStatus(status),
                value: status,
              })
            )}
            requiredRule={undefined}
          />
        )}

        {isAnnounceMode && (
          <ControlledMultipleFreeSoloAutoCompleteWithCategories
            sx={spacingItem}
            options={characteristicsOptions}
            freeSolo={true}
            control={control}
            error={undefined}
            fieldName="characteristics"
            label="Caractéristiques de l'animal"
            requiredRule={undefined}
          />
        )}

        <Typography sx={{ ...spacingItem, mb: -1, color: '#7A8197', fontSize: 13.5, lineHeight: 1.5 }}>
          <Box component="span" sx={{ fontWeight: 700, color: colors.cello }}>
            Astuce :
          </Box>{' '}
          Vous pourrez modifier ou ajouter des informations dans la fiche de l&apos;animal ultérieurement.
        </Typography>

        <TextField
          id="description-input"
          label="Description"
          type="text"
          fullWidth
          {...register('description')}
          sx={spacingItem}
          error={!!errors.description}
          helperText={errors.description?.message}
          multiline
          rows={4}
        />

        {!isAnnounceMode && (
          <TextField
            id="lifeBeforeAssociation-input"
            label="Notes et parcours avant association"
            type="text"
            fullWidth
            {...register('lifeBeforeAssociation')}
            sx={spacingItem}
            error={!!errors.lifeBeforeAssociation}
            helperText={errors.lifeBeforeAssociation?.message}
            multiline
            rows={4}
          />
        )}

        {serverError && (
          <Box sx={spacingItem}>
            <Typography color="error">{serverError}</Typography>
          </Box>
        )}

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            justifyContent: { xs: 'center', md: 'flex-end' },
            gap: 2,
            ...spacingItem,
          }}
        >
          {isAnnounceMode ? (
            <Tooltip
              title={isPublishAnnounceDisabled && isDesktop ? 'Des informations essentielles sont manquantes' : ''}
              placement="top"
              arrow
            >
              <Box sx={{ width: '100%' }} onClick={!isDesktop ? handleDisabledPublishClick : undefined}>
                <span style={{ display: 'block', width: '100%' }}>
                  <Button
                    variant="contained"
                    sx={{
                      width: '100%',
                      minHeight: 46,
                      px: 3,
                      borderRadius: '999px',
                      fontWeight: 700,
                      boxShadow: 'none',
                    }}
                    type="submit"
                    disabled={isPublishAnnounceDisabled}
                  >
                    Publier l&apos;annonce
                  </Button>
                </span>
              </Box>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              sx={{
                width: { xs: '100%', md: 'fit-content' },
                minWidth: { md: 260 },
                minHeight: 46,
                px: 3,
                borderRadius: '999px',
                fontWeight: 700,
                boxShadow: 'none',
                order: { xs: 1, md: 2 },
              }}
              type="submit"
            >
              Créer et aller sur sa fiche
            </Button>
          )}
          {!isAnnounceMode && (
            <Button
              variant="outlined"
              sx={{
                width: { xs: '100%', md: 'fit-content' },
                minWidth: { md: 320 },
                minHeight: 46,
                px: 3,
                borderRadius: '999px',
                fontWeight: 700,
                boxShadow: 'none',
                order: { xs: 2, md: 1 },
              }}
              type="button"
              onClick={() => onSubmit(watch(), true)}
            >
              Créer et ajouter un autre animal à partir de celui ci
            </Button>
          )}
        </Box>
        {isAnnounceMode && (
          <Box
            sx={{
              ...surfaceCardSx,
              mt: 1.5,
              p: { xs: 1.5, md: 1.75 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 0.75, md: 2 },
              }}
            >
              <Typography sx={{ fontWeight: 800, color: colors.cello, flexShrink: 0 }}>Modes de diffusion</Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 0.25, sm: 2.5 },
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={watch('shareOnPetso') ?? true}
                      onChange={(_, checked) => setValue('shareOnPetso', checked)}
                      color="primary"
                    />
                  }
                  label="Partager sur Petso"
                  sx={{ m: 0 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={watch('shareOnWamiz') ?? true}
                      onChange={(_, checked) => setValue('shareOnWamiz', checked)}
                      color="primary"
                    />
                  }
                  label="Partager sur Wamiz"
                  sx={{ m: 0 }}
                />
              </Box>
            </Box>
          </Box>
        )}
        <FormHelperText>
          {isAnnounceMode
            ? "Vous pourrez enrichir la fiche de l'animal juste après la création si vous souhaitez aller plus loin."
            : `Vous pourrez modifier ou ajouter des informations dans la fiche de l'animal ultérieurement.`}
        </FormHelperText>
      </form>
    </Box>
  )
}

const characteristicsOptions = [
  {
    title: 'Habitat et mode de vie',
    options: [
      'Convient à la vie en appartement',
      'Ne convient pas à la vie en appartement',
      'Convient à la vie en maison',
      'Ne convient pas à la vie en maison',
      'Hypoallergénique',
    ],
  },
  {
    title: 'Vie sociale',
    options: [
      'Entente avec les enfants',
      'Ne s’entend pas avec les enfants',
      'Entente avec les chiens',
      'Ne s’entend pas avec les chiens',
      'Entente avec les chats',
      'Ne s’entend pas avec les chats',
      'Entente avec les autres animaux',
      'Ne s’entend pas avec les autres animaux',
    ],
  },
  {
    title: 'Traits de caractère',
    options: ['Joueur', 'Calin', 'Craintif', 'Peureux', 'Agressif', 'Timide', 'Bruyant', 'Propre'],
  },
]
